import React, { ReactNode, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { ApiEndpoints, Roles } from "../models/userData";
import { useIntl } from "react-intl";
import { markAllNotficationsAsRead, markReadNotification } from "../api/notifications";
import { showBackendMessage } from "../helpers/messagesHelper";
import { useSocket } from "../hooks/useSocket";
import Messages from "../localization/Messages";
import { INotification } from "../models/notification";
import { useAuth } from "../hooks/useAuth";
import { useFetchNotifications } from "../../src/hooks/useFetchNotifications";
import { getChangedValue } from "../helpers/stateChangeHelper";
import { isAxiosError } from "axios";

/**
 * NotificationsContextProps interface represents the shape of the notifications context.
 */

export type NotificationsContextProps = {
    notifications: INotification[];
    unread: number;
    markAsRead: (notificationId: string) => Promise<void>;
    markAllAsRead: () => Promise<void>;
};

/**
 * NotificationsContext is a React context that provides notifications data and functionality.
 */
export const NotificationsContext = React.createContext<NotificationsContextProps | undefined>(
    undefined,
);

/**
 * NotificationsProvider is a React component that wraps its children with the NotificationsContext.Provider.
 * It manages the state of notifications and provides methods to mark notifications as read.
 *
 * @param children - The child components to be wrapped by the NotificationsProvider.
 */
export function NotificationsProvider({ children }: { children: ReactNode }) {
    const intl = useIntl();
    const dispatch = useDispatch();
    const { data: notificationsData } = useFetchNotifications({
        limit: 200,
        filter: {},
        sortOrder: ["createTs desc"],
    });

    const [notifications, setNotifications] = useState<INotification[]>(notificationsData ?? []);

    const { lastMessage: lastNotification } = useSocket("Notification");
    const { user } = useAuth();
    const userRoles = user.roles;

    const unread = notifications.filter(({ read }) => !read).length;

    const isDispatcher = userRoles.includes(Roles.PASSENGER_CABIN_CRUISES_DISPATCHER);
    const isDocks = userRoles.includes(Roles.LOCKS_AND_DOCKS_DISPATCHER);

    const apiEndpoint = useMemo(() => {
        const endpoint = isDispatcher ? ApiEndpoints.dispatcher : isDocks ? ApiEndpoints.docks : "";
        return endpoint;
    }, [isDispatcher, isDocks]);

    useEffect(() => {
        if (!notificationsData) return;
        setNotifications(prev => getChangedValue<INotification[]>(prev, notificationsData));
    }, [notificationsData]);

    useEffect(() => {
        if (!lastNotification) return;
        setNotifications(prev => [...prev, lastNotification]);
    }, [lastNotification]);

    const markAsRead = useCallback(
        async (notificationId: string) => {
            try {
                await markReadNotification(apiEndpoint, notificationId);
                setNotifications(prev =>
                    prev.map(n => (n.id === notificationId ? { ...n, read: true } : n)),
                );
            } catch (error) {
                // A notification can disappear after this panel was loaded (or belong to an old login).
                if (isAxiosError(error) && error.response?.status === 404) {
                    setNotifications(prev => prev.filter(n => n.id !== notificationId));
                    return;
                }
                dispatch(showBackendMessage(intl, "error", "updating", Messages.notifications));
            } finally {
                // NOOP
            }
        },
        [apiEndpoint, dispatch, intl, setNotifications],
    );

    const markAllAsRead = useCallback(async () => {
        try {
            await markAllNotficationsAsRead(apiEndpoint);

            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch {
            dispatch(showBackendMessage(intl, "error", "updating", Messages.notifications));
        } finally {
            // NOOP
        }
    }, [apiEndpoint, dispatch, intl]);

    return (
        <NotificationsContext.Provider value={{ notifications, unread, markAsRead, markAllAsRead }}>
            {children}
        </NotificationsContext.Provider>
    );
}
